// Utility functions for exporting budget data to PDF and Excel formats
export const exportToPDF = (orcamento: any) => {
  try {
    // Create a simple HTML structure for PDF
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Por favor, permita pop-ups para exportar o PDF')
      return
    }

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('pt-BR')
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Orçamento ${orcamento.numero}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .info-section { margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .etapa { margin-bottom: 20px; border: 1px solid #ddd; padding: 10px; }
          .etapa-header { background-color: #f5f5f5; padding: 10px; font-weight: bold; }
          .subetapa { margin-left: 20px; margin-bottom: 10px; }
          .subetapa-header { background-color: #f9f9f9; padding: 5px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
          .totals { margin-top: 30px; border: 2px solid #333; padding: 15px; }
          .total-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .total-final { font-weight: bold; font-size: 18px; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ORÇAMENTO</h1>
          <h2>${orcamento.numero}</h2>
        </div>
        
        <div class="info-section">
          <h3>Informações do Cliente</h3>
          <div class="info-row">
            <span><strong>Cliente:</strong> ${orcamento.nomeCliente}</span>
            <span><strong>Contato:</strong> ${orcamento.contatoCliente || 'N/A'}</span>
          </div>
          <div class="info-row">
            <span><strong>Projeto:</strong> ${orcamento.nomeProjeto}</span>
            <span><strong>Status:</strong> ${orcamento.status}</span>
          </div>
          <div class="info-row">
            <span><strong>Data de Emissão:</strong> ${formatDate(orcamento.dataEmissao)}</span>
            <span><strong>Validade:</strong> ${formatDate(orcamento.dataValidade)}</span>
          </div>
          ${orcamento.descricao ? `<div style="margin-top: 10px;"><strong>Descrição:</strong> ${orcamento.descricao}</div>` : ''}
        </div>

        <div class="etapas-section">
          <h3>Etapas do Projeto</h3>
          ${orcamento.etapas?.map((etapa: any) => `
            <div class="etapa">
              <div class="etapa-header">${etapa.codigo} - ${etapa.nome}</div>
              ${etapa.subetapas?.map((subetapa: any) => `
                <div class="subetapa">
                  <div class="subetapa-header">${subetapa.codigo} - ${subetapa.nome}</div>
                  ${subetapa.itens?.length > 0 ? `
                    <table>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Tipo</th>
                          <th>Unidade</th>
                          <th>Quantidade</th>
                          <th>Preço Unitário</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${subetapa.itens.map((item: any) => `
                          <tr>
                            <td>${item.nome}</td>
                            <td>${item.tipo}</td>
                            <td>${item.unidadeMedida}</td>
                            <td>${item.quantidade}</td>
                            <td>${formatCurrency(item.precoUnitario)}</td>
                            <td>${formatCurrency(item.subtotal)}</td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                  ` : '<p>Nenhum item nesta subetapa</p>'}
                </div>
              `).join('') || '<p>Nenhuma subetapa</p>'}
            </div>
          `).join('') || '<p>Nenhuma etapa definida</p>'}
        </div>

        <div class="totals">
          <h3>Resumo Financeiro</h3>
          <div class="total-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(orcamento.valorSubtotal || 0)}</span>
          </div>
          <div class="total-row">
            <span>BDI (${orcamento.bdi || 0}%):</span>
            <span>+ ${formatCurrency(((orcamento.valorSubtotal || 0) * (orcamento.bdi || 0)) / 100)}</span>
          </div>
          <div class="total-row">
            <span>Desconto (${orcamento.desconto || 0}%):</span>
            <span>- ${formatCurrency(((orcamento.valorSubtotal || 0) * (orcamento.desconto || 0)) / 100)}</span>
          </div>
          <hr>
          <div class="total-row total-final">
            <span>TOTAL:</span>
            <span>${formatCurrency(orcamento.valorTotal || 0)}</span>
          </div>
        </div>

        ${orcamento.observacoes ? `
          <div style="margin-top: 20px;">
            <h3>Observações</h3>
            <p>${orcamento.observacoes}</p>
          </div>
        ` : ''}

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            }
          }
        </script>
      </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
  } catch (error) {
    console.error('Erro ao exportar PDF:', error)
    alert('Erro ao exportar PDF. Tente novamente.')
  }
}

export const exportToExcel = (orcamento: any) => {
  try {
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('pt-BR')
    }

    let csvContent = ''
    
    // Cabeçalho das informações do orçamento
    csvContent += 'INFORMAÇÕES DO ORÇAMENTO\n'
    csvContent += 'Campo,Valor\n'
    csvContent += `Número,${orcamento.numero}\n`
    csvContent += `Cliente,${orcamento.nomeCliente}\n`
    csvContent += `Contato,${orcamento.contatoCliente || 'N/A'}\n`
    csvContent += `Projeto,${orcamento.nomeProjeto}\n`
    csvContent += `Status,${orcamento.status}\n`
    csvContent += `Data de Emissão,${formatDate(orcamento.dataEmissao)}\n`
    csvContent += `Validade,${formatDate(orcamento.dataValidade)}\n`
    if (orcamento.descricao) {
      csvContent += `Descrição,"${orcamento.descricao}"\n`
    }
    csvContent += '\n\n'

    // Cabeçalho da tabela de itens
    csvContent += 'ITENS DO ORÇAMENTO\n'
    csvContent += 'Código Etapa,Nome Etapa,Código Subetapa,Nome Subetapa,Item,Tipo,Unidade,Quantidade,Preço Unitário,Subtotal\n'

    // Dados dos itens em formato tabular
    if (orcamento.etapas && orcamento.etapas.length > 0) {
      orcamento.etapas.forEach((etapa: any) => {
        if (etapa.subetapas && etapa.subetapas.length > 0) {
          etapa.subetapas.forEach((subetapa: any) => {
            if (subetapa.itens && subetapa.itens.length > 0) {
              subetapa.itens.forEach((item: any) => {
                csvContent += `${etapa.codigo},"${etapa.nome}",${subetapa.codigo},"${subetapa.nome}","${item.nome}",${item.tipo},${item.unidadeMedida},${item.quantidade || 0},${item.precoUnitario || 0},${item.subtotal || 0}\n`
              })
            }
          })
        }
      })
    }

    csvContent += '\n\n'

    // Resumo financeiro em formato tabular
    csvContent += 'RESUMO FINANCEIRO\n'
    csvContent += 'Descrição,Valor\n'
    csvContent += `Subtotal,${orcamento.valorSubtotal || 0}\n`
    csvContent += `BDI (${orcamento.bdi || 0}%),${((orcamento.valorSubtotal || 0) * (orcamento.bdi || 0)) / 100}\n`
    csvContent += `Desconto (${orcamento.desconto || 0}%),${((orcamento.valorSubtotal || 0) * (orcamento.desconto || 0)) / 100}\n`
    csvContent += `TOTAL,${orcamento.valorTotal || 0}\n`

    if (orcamento.observacoes) {
      csvContent += '\n\n'
      csvContent += 'OBSERVAÇÕES\n'
      csvContent += `"${orcamento.observacoes}"\n`
    }

    // Criar e baixar o arquivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `orcamento_${orcamento.numero}_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      alert('Seu navegador não suporta download automático. Por favor, copie o conteúdo e salve manualmente.')
    }
  } catch (error) {
    console.error('Erro ao exportar Excel:', error)
    alert('Erro ao exportar Excel. Tente novamente.')
  }
}
